import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Full procedure (CPT) catalog loader with a module-level cache. Backs the
 * "load once, filter locally" flow on the ProcedureCatalogAutocomplete —
 * one fetch per page-load; every subsequent picker open renders instantly
 * against the cached array.
 *
 * Response contract: `{data: [{code, name}]}` from
 * `GET /api/v1/procedure-catalog/all`. Only `{code, name}` is surfaced —
 * everything else is dropped so the retained heap stays tight.
 *
 * At ~11,520 rows the payload is a few hundred KB gzipped and ~2–4 MB
 * of heap after parse; well within mobile budgets. See the design
 * discussion in the LI project memory for the sizing math.
 */

let CACHE = null;
let INFLIGHT = null;

export const getCachedProcedureCatalog = () => CACHE;

export const fetchProcedureCatalog = () => {
  if (CACHE) return Promise.resolve(CACHE);
  if (INFLIGHT) return INFLIGHT;
  INFLIGHT = new Promise((resolve) => {
    const svc = new APIService();
    svc.get(
      APIConfig.procedureCatalog.all,
      (res) => {
        const rows = (res && Array.isArray(res.data) ? res.data : [])
          // Drop garbage rows early so downstream code doesn't need to
          // guard against them on every keystroke.
          .filter((r) => r && r.name)
          .map((r) => ({
            code: (r.code || "").toString().trim(),
            name: (r.name || "").toString().trim(),
          }));
        CACHE = rows;
        INFLIGHT = null;
        resolve(rows);
      },
      () => {
        // Failure leaves the cache empty so a future call retries — but
        // resolve empty so the current picker just shows "No matches"
        // instead of hanging on a loader.
        INFLIGHT = null;
        resolve([]);
      },
    );
  });
  return INFLIGHT;
};
